/* ==========================================================================
   NGA — Wishlist

   Storage-backed (localStorage), no app or account required, so it works
   for logged-out shoppers — which is most of them.

   Stored records keep only a handle + a snapshot for instant paint. The
   wishlist page then re-fetches /products/{handle}.js so price, availability
   and imagery are never stale.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'nga:wishlist';
  var EVENT = 'nga:wishlist:change';

  var NGA = (window.NGA = window.NGA || {});

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (e) { return []; }
  }

  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* quota / private mode */ }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: list } }));
  }

  function has(handle) {
    return read().some(function (i) { return i.handle === handle; });
  }

  function add(item) {
    if (!item || !item.handle) return read();
    var list = read();
    if (list.some(function (i) { return i.handle === item.handle; })) return list;
    list.unshift({
      handle: item.handle,
      title: item.title || '',
      url: item.url || ('/products/' + item.handle),
      image: item.image || '',
      price: item.price || null,
      addedAt: Date.now()
    });
    write(list);
    return list;
  }

  function remove(handle) {
    var list = read().filter(function (i) { return i.handle !== handle; });
    write(list);
    return list;
  }

  function toggle(item) {
    if (has(item.handle)) { remove(item.handle); return false; }
    add(item);
    return true;
  }

  function clear() { write([]); }

  NGA.wishlist = { read: read, add: add, remove: remove, toggle: toggle, has: has, clear: clear, EVENT: EVENT };

  /* ---------------------------------------------------------------------
     Header count
     --------------------------------------------------------------------- */
  function paintCount() {
    var n = read().length;
    document.querySelectorAll('[data-nga-wishlist-count]').forEach(function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
      if (n > 0) {
        el.classList.remove('is-bump');
        // Force reflow so the animation retriggers on every change.
        void el.offsetWidth;
        el.classList.add('is-bump');
      }
    });
  }

  /* ---------------------------------------------------------------------
     Toggle buttons — anywhere in the theme
     --------------------------------------------------------------------- */
  function paintButtons() {
    document.querySelectorAll('[data-nga-wish]').forEach(function (btn) {
      var handle = btn.getAttribute('data-nga-wish');
      var on = has(handle);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = on ? 'Remove from wishlist' : 'Add to wishlist';
      btn.setAttribute('aria-label', label);
      var text = btn.querySelector('[data-nga-wish-text]');
      if (text) text.textContent = on ? 'Saved' : 'Save';
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-nga-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-nga-wish');
    var added = toggle({
      handle: handle,
      title: btn.getAttribute('data-nga-wish-title') || '',
      url: btn.getAttribute('data-nga-wish-url') || ('/products/' + handle),
      image: btn.getAttribute('data-nga-wish-image') || '',
      price: btn.getAttribute('data-nga-wish-price') || null
    });

    if (NGA.toast) {
      NGA.toast(
        added
          ? 'Saved to your wishlist. <a href="/pages/wishlist">View</a>'
          : 'Removed from your wishlist.',
        { variant: added ? 'pink' : null }
      );
    }
  });

  document.addEventListener(EVENT, function () { paintCount(); paintButtons(); });
  document.addEventListener('nga:refresh', function () { paintButtons(); });

  // Keep tabs in sync.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { paintCount(); paintButtons(); renderPage(); }
  });

  /* ---------------------------------------------------------------------
     Wishlist page renderer
     --------------------------------------------------------------------- */
  function money(cents) {
    if (cents == null) return '';
    var n = typeof cents === 'string' ? parseFloat(cents) : cents;
    if (isNaN(n)) return '';
    return '$' + (n / 100).toFixed(2).replace(/\.00$/, '');
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderPage() {
    var root = document.querySelector('[data-nga-wishlist-grid]');
    if (!root) return;

    var empty = document.querySelector('[data-nga-wishlist-empty]');
    var toolbar = document.querySelector('[data-nga-wishlist-toolbar]');
    var countEl = document.querySelector('[data-nga-wishlist-total]');
    var list = read();

    if (countEl) countEl.textContent = list.length;
    if (empty) empty.hidden = list.length > 0;
    if (toolbar) toolbar.hidden = list.length === 0;

    if (!list.length) { root.innerHTML = ''; return; }

    // Paint the snapshot immediately, then refresh each card from the live product.
    root.innerHTML = list.map(function (i) {
      return (
        '<article class="nga-wish-card" data-nga-wish-card="' + esc(i.handle) + '">' +
          '<a class="nga-wish-card__media nga-media nga-ratio-4-5 nga-zoom" href="' + esc(i.url) + '">' +
            (i.image ? '<img src="' + esc(i.image) + '" alt="" loading="lazy" decoding="async">' : '') +
          '</a>' +
          '<button type="button" class="nga-wish-card__remove" data-nga-wish-remove="' + esc(i.handle) + '" aria-label="Remove ' + esc(i.title) + ' from wishlist">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
          '</button>' +
          '<div class="nga-wish-card__body">' +
            '<h3 class="nga-wish-card__title"><a href="' + esc(i.url) + '">' + esc(i.title) + '</a></h3>' +
            '<p class="nga-wish-card__price" data-nga-wish-price>' + esc(money(i.price)) + '</p>' +
            '<div class="nga-wish-card__sizes" data-nga-wish-sizes></div>' +
            '<button type="button" class="nga-btn nga-btn--sm nga-btn--block nga-wish-card__atc" data-nga-wish-atc="' + esc(i.handle) + '" disabled>Loading…</button>' +
          '</div>' +
        '</article>'
      );
    }).join('');

    list.forEach(function (i) { hydrate(i.handle); });
    document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { root: root } }));
  }

  var hydrated = {};

  function hydrate(handle) {
    var card = document.querySelector('[data-nga-wish-card="' + handle + '"]');
    if (!card) return;

    function paint(p) {
      if (!p) return;
      var priceEl = card.querySelector('[data-nga-wish-price]');
      var sizesEl = card.querySelector('[data-nga-wish-sizes]');
      var atc = card.querySelector('[data-nga-wish-atc]');

      if (priceEl) {
        var html = money(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          html += ' <s>' + money(p.compare_at_price) + '</s>';
        }
        priceEl.innerHTML = html;
      }

      var available = p.variants.filter(function (v) { return v.available; });

      if (sizesEl && p.variants.length > 1) {
        sizesEl.innerHTML = p.variants.map(function (v) {
          return '<button type="button" class="nga-wish-size' + (v.available ? '' : ' is-out') + '" ' +
            'data-nga-wish-variant="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' +
            esc(v.title) + '</button>';
        }).join('');
        var first = sizesEl.querySelector('.nga-wish-size:not(.is-out)');
        if (first) first.classList.add('is-active');
      }

      if (atc) {
        if (!available.length) {
          atc.textContent = 'Sold out';
          atc.disabled = true;
          atc.classList.add('nga-btn--ghost');
        } else {
          atc.textContent = 'Add to bag';
          atc.disabled = false;
          atc.setAttribute('data-variant-id', (sizesEl && sizesEl.querySelector('.nga-wish-size.is-active'))
            ? sizesEl.querySelector('.nga-wish-size.is-active').getAttribute('data-nga-wish-variant')
            : available[0].id);
        }
      }
    }

    if (hydrated[handle]) { paint(hydrated[handle]); return; }

    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('gone');
        return r.json();
      })
      .then(function (p) { hydrated[handle] = p; paint(p); })
      .catch(function () {
        // Product deleted or unpublished — say so rather than showing a dead card.
        var atc = card.querySelector('[data-nga-wish-atc]');
        if (atc) { atc.textContent = 'No longer available'; atc.disabled = true; }
      });
  }

  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-nga-wish-remove]');
    if (removeBtn) {
      remove(removeBtn.getAttribute('data-nga-wish-remove'));
      renderPage();
      return;
    }

    var sizeBtn = e.target.closest('[data-nga-wish-variant]');
    if (sizeBtn && !sizeBtn.disabled) {
      var wrap = sizeBtn.parentElement;
      wrap.querySelectorAll('.nga-wish-size').forEach(function (b) { b.classList.remove('is-active'); });
      sizeBtn.classList.add('is-active');
      var card = sizeBtn.closest('[data-nga-wish-card]');
      var atcBtn = card && card.querySelector('[data-nga-wish-atc]');
      if (atcBtn) atcBtn.setAttribute('data-variant-id', sizeBtn.getAttribute('data-nga-wish-variant'));
      return;
    }

    var atc = e.target.closest('[data-nga-wish-atc]');
    if (atc && !atc.disabled) {
      var id = atc.getAttribute('data-variant-id');
      if (!id) return;
      var original = atc.textContent;
      atc.disabled = true;
      atc.textContent = 'Adding…';

      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.description || 'Could not add');
          atc.textContent = 'Added';
          if (NGA.toast) NGA.toast('Added to your bag. <a href="/cart">View bag</a>', { variant: 'pink' });
          document.dispatchEvent(new CustomEvent('nga:cart:updated'));
          setTimeout(function () { atc.textContent = original; atc.disabled = false; }, 1600);
        })
        .catch(function (err) {
          atc.textContent = original;
          atc.disabled = false;
          if (NGA.toast) NGA.toast(esc(err.message || 'Could not add to bag.'));
        });
    }

    if (e.target.closest('[data-nga-wishlist-clear]')) {
      if (window.confirm('Remove everything from your wishlist?')) { clear(); renderPage(); }
    }
  });

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() { paintCount(); paintButtons(); renderPage(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
